import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ScrollArea,
} from "@wove/web-ui";
import { Loader2, Search } from "lucide-react";

interface HsCodeSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDescription: string;
  onSelect: (hsCode: string) => void;
  onSearch: (query: string) => Promise<Array<{ code: string; description: string }>>;
}

export default function HsCodeSearchDialog({
  open,
  onOpenChange,
  initialDescription,
  onSelect,
  onSearch,
}: HsCodeSearchDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [data, setData] = useState<Array<{ code: string; description: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize search term with the item description when dialog opens
  useEffect(() => {
    if (open && initialDescription) {
      setSearchTerm(initialDescription);
      setDebouncedSearchTerm(initialDescription);
    }
  }, [open, initialDescription]);

  // Update debounced search term after 500ms of inactivity
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Fetch similar HS codes based on the description
  useEffect(() => {
    if (!debouncedSearchTerm || !open) return;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const results = await onSearch(debouncedSearchTerm);
        setData(results || []);
      } catch (err) {
        setError("Error loading HS codes. Please try again.");
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debouncedSearchTerm, open, onSearch]);

  const handleSelect = (hsCode: string) => {
    onSelect(hsCode);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Search HS Codes</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 my-4 flex-shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
          <Button variant="outline" onClick={() => setDebouncedSearchTerm(searchTerm)} disabled={isLoading}>
            Search
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8 border rounded-md">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500 border rounded-md">{error}</div>
        ) : data.length === 0 ? (
          <div className="text-center py-8 text-gray-500 border rounded-md">
            No HS codes found for this description. Try a different search term.
          </div>
        ) : (
          <>
            <div style={{ 
              maxHeight: 'calc(70vh - 200px)', 
              minHeight: '300px',
              overflowY: 'auto', 
              border: '1px solid #e5e7eb', 
              borderRadius: '0.375rem' 
            }}>
              <table className="w-full">
                <thead className="sticky top-0 bg-white border-b">
                  <tr>
                    <th className="text-left p-2 font-medium text-sm" style={{ width: '120px' }}>HS Code</th>
                    <th className="text-left p-2 font-medium text-sm">Description</th>
                    <th className="text-right p-2 font-medium text-sm" style={{ width: '100px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => (
                    <tr key={item.code} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-mono text-sm">{item.code}</td>
                      <td className="p-2 text-sm">{item.description}</td>
                      <td className="p-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleSelect(item.code)}>
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Debug info */}
            <div className="text-xs text-gray-500 px-2 mt-2">
              Showing {data.length} results
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}